/* Label placement for the booking map polygons.

   Neither a plain vertex average nor the area-weighted centroid feels "in the
   middle" for the long thin wedges merchants draw for villas and campsites:
   both are pulled toward whichever end carries more mass, which for an extreme
   sliver can land near a corner instead of the visually central point. We use
   the "pole of inaccessibility" instead -- the point deepest inside the shape,
   furthest from every edge -- which is what map-label tools (e.g. Mapbox's
   polylabel) use for exactly this problem. */

const COARSE_STEPS = 12;
const FINE_STEPS = 6;
const REFINE_PASSES = 5;

function polygonAreaCentroid(points) {
  let signedArea = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const cross = current.x * next.y - next.x * current.y;
    signedArea += cross;
    cx += (current.x + next.x) * cross;
    cy += (current.y + next.y) * cross;
  }
  signedArea = signedArea / 2;
  if (Math.abs(signedArea) < 1e-9) return null;
  return { x: cx / (6 * signedArea), y: cy / (6 * signedArea) };
}

// Standard ray-casting point-in-polygon test.
function isPointInPolygon(point, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const pi = points[i];
    const pj = points[j];
    const intersects =
      pi.y > point.y !== pj.y > point.y &&
      point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointToSegmentDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;
  let ratio = lengthSq === 0 ? 0 : ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq;
  ratio = Math.max(0, Math.min(1, ratio));
  const closestX = start.x + ratio * dx;
  const closestY = start.y + ratio * dy;
  const deltaX = point.x - closestX;
  const deltaY = point.y - closestY;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}

function distanceToPolygonEdges(point, points) {
  let minDistance = Infinity;
  for (let i = 0; i < points.length; i++) {
    const distance = pointToSegmentDistance(point, points[i], points[(i + 1) % points.length]);
    if (distance < minDistance) minDistance = distance;
  }
  return minDistance;
}

// Grid-search approximation of the pole of inaccessibility.
function poleOfInaccessibility(points) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  points.forEach((point) => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });
  const width = maxX - minX;
  const height = maxY - minY;
  if (!(width > 0) || !(height > 0)) return null;

  let best = null;
  let bestDistance = -Infinity;
  const evaluate = (x, y) => {
    const candidate = { x: x, y: y };
    if (!isPointInPolygon(candidate, points)) return;
    const distance = distanceToPolygonEdges(candidate, points);
    if (distance > bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  };

  // Seed with the area centroid -- usually already a decent candidate, which
  // improves the odds the search converges on the true deepest point rather
  // than a grid-aligned approximation of it.
  const seed = polygonAreaCentroid(points);
  if (seed) evaluate(seed.x, seed.y);

  for (let i = 0; i <= COARSE_STEPS; i++) {
    for (let j = 0; j <= COARSE_STEPS; j++) {
      evaluate(minX + (width * i) / COARSE_STEPS, minY + (height * j) / COARSE_STEPS);
    }
  }
  if (!best) return null;

  let radiusX = width / COARSE_STEPS;
  let radiusY = height / COARSE_STEPS;
  for (let pass = 0; pass < REFINE_PASSES; pass++) {
    const centerX = best.x;
    const centerY = best.y;
    for (let i = -FINE_STEPS; i <= FINE_STEPS; i++) {
      for (let j = -FINE_STEPS; j <= FINE_STEPS; j++) {
        evaluate(centerX + (radiusX * i) / FINE_STEPS, centerY + (radiusY * j) / FINE_STEPS);
      }
    }
    radiusX /= 2;
    radiusY /= 2;
  }
  return best;
}

function nearestVertexToAverage(points) {
  const sum = points.reduce((acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }), { x: 0, y: 0 });
  const average = { x: sum.x / points.length, y: sum.y / points.length };
  let closest = points[0];
  let closestDistance = Infinity;
  points.forEach((point) => {
    const distance =
      (point.x - average.x) * (point.x - average.x) + (point.y - average.y) * (point.y - average.y);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = point;
    }
  });
  return closest;
}

/**
 * Best point to anchor a polygon's marker/label on.
 * @param {Array<{x: number, y: number}>} points polygon vertices, normalised 0..1
 * @returns {{x: number, y: number}|null} anchor point, or null for an empty polygon
 */
export function getPolygonLabelPoint(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  return poleOfInaccessibility(points) || nearestVertexToAverage(points);
}

/**
 * Polygon vertices as an SVG `points` attribute in a 0..100 viewBox.
 * @param {Array<{x: number, y: number}>} points polygon vertices, normalised 0..1
 * @returns {string} space-separated "x,y" pairs
 */
export function toSvgPoints(points) {
  if (!Array.isArray(points)) return '';
  return points.map((point) => point.x * 100 + ',' + point.y * 100).join(' ');
}

/* `stroke-linejoin: round` only softens the outline where it turns -- the fill
   still follows the polygon's raw, sharp vertices, so a low-opacity area reads
   as sharp-cornered no matter what the stroke does. Actually rounding the
   shape means replacing each vertex with a short quadratic curve, the same
   "corner cutting" technique used for rounded-rectangle-ish blobs. */
const CORNER_RATIO = 0.3;

/**
 * Polygon vertices as an SVG `path` `d` string, in a 0..100 viewBox, with
 * every corner replaced by a quadratic curve.
 * @param {Array<{x: number, y: number}>} points polygon vertices, normalised 0..1
 * @returns {string} an SVG path `d` attribute, or '' for a degenerate polygon
 */
export function toRoundedSvgPath(points) {
  if (!Array.isArray(points) || points.length < 3) return '';
  const scaled = points.map((point) => ({ x: point.x * 100, y: point.y * 100 }));
  const count = scaled.length;

  // For each vertex, the two points it gets cut back to along its own edges --
  // clamped to half an edge's length so two rounded corners on a short edge
  // never overlap.
  const corners = scaled.map((curr, i) => {
    const prev = scaled[(i - 1 + count) % count];
    const next = scaled[(i + 1) % count];
    return { curr: curr, before: cutCorner(curr, prev), after: cutCorner(curr, next) };
  });

  let path = 'M ' + corners[0].before.x + ' ' + corners[0].before.y + ' ';
  for (let i = 0; i < count; i++) {
    const corner = corners[i];
    path += 'Q ' + corner.curr.x + ' ' + corner.curr.y + ' ' + corner.after.x + ' ' + corner.after.y + ' ';
    const nextBefore = corners[(i + 1) % count].before;
    path += 'L ' + nextBefore.x + ' ' + nextBefore.y + ' ';
  }
  return path + 'Z';
}

function cutCorner(from, towards) {
  const dx = towards.x - from.x;
  const dy = towards.y - from.y;
  const edgeLength = Math.sqrt(dx * dx + dy * dy);
  if (edgeLength === 0) return { x: from.x, y: from.y };
  const cut = Math.min(edgeLength * CORNER_RATIO, edgeLength / 2);
  return { x: from.x + (dx / edgeLength) * cut, y: from.y + (dy / edgeLength) * cut };
}



// WEBPACK FOOTER //
// ./src/components/hotel/be/v3/digital-map/polygonLabel.js